// @flow

import React from 'react';
import { observer } from 'mobx-react';
import Select from 'react-select';
import Helmet from 'react-helmet';
import { css } from 'glamor';
import { Col, Row, Clearfix } from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';
import moment from 'moment';
import min from 'lodash/min';
import max from 'lodash/max';
import range from 'lodash/range';

import Title from '~/components/global/Title';
import SearchPanel from '~/components/global/SearchPanel';
import SearchInput from '~/components/global/SearchInput';
import ReviewList from '~/components/content/review/ReviewList';
import Genre from '~/components/content/game/Genre';
import Contributor from '~/components/global/Contributor';
import Loader from '~/components/global/Loader';
import SearchLetter from '~/components/global/SearchLetter';
import PlatformDetails from '~/components/content/platform/PlatformDetails';
import type {
    Platform as PlatformType,
    User as UserType,
    Genre as GenreType,
    Company as CompanyType,
    Review as ReviewType,
    SelectOption,
} from '~/types';

const WEBSITE_BASE_URL = (function() {
    if (typeof location !== 'undefined') {
        return location.href;
    }

    return process.env.NODE_ENV === 'production' ?
        'https://www.supagemu.com' :
        'http://localhost:4002';
})();

const containerRule = css({});

const searchPanelRule = css({
    marginBottom: '15px',
});

const topBufferRule = css({
    marginTop: '25px',

    '@media(max-width: 768px)': {
        marginTop: 0,
    },
});

const searchInputRule = css({
    lineHeight: '35px',
});

const subtitleRule = css({
    marginTop: '25px',
    marginBottom: '25px',
});

const lettersRule = css({
    marginTop: '25px',
    width: '100%',
    paddingLeft: 0,
    listStyle: 'none',
    textAlign: 'center',
});

const letterItemRule = css({
    display: 'inline-block',
    paddingRight: '2%',
});

const contributorRule = css({
    marginTop: '-8px',
});

const inputSpacer = css({
    '@media(max-width: 768px)': {
        marginBottom: '15px',
    },
});

const advancedCriteria = css({
    '@media(max-width: 768px)': {
        display: 'none',
    },
});

const moreCriteria = css({
    display: 'none',
    '@media(max-width: 768px)': {
        display: 'block',

        '& span': {
            marginRight: '5px',
            color: 'rgb(228, 65, 34)',
        },

        '& a': {
            color: 'rgb(51, 51, 51)',
        },
    },
});

const lettersContainer = css({
    marginTop: '-25px',
    marginBottom: '-20px',
});

const inlineLoader = css({
    position: 'absolute',
    right: '10px',
    marginTop: '15px',
});

@observer
export default class PlatformMain extends React.Component {
    props: {
        author: UserType,
        authors: Array<UserType>,
        developer: Array<CompanyType>,
        developers: Array<CompanyType>,
        genre: GenreType,
        genres: Array<GenreType>,
        moreCriteriaVisible: boolean,
        onInputChange: () => void,
        onLetterClick: () => void,
        onToggleMoreCriteria: () => void,
        platform: PlatformType,
        reviews: Array<ReviewType>,
        selectedLetter: string,
        year: SelectOption,
    };

    render() {
        const {
            author,
            authors,
            developer,
            developers,
            genre,
            genres,
            moreCriteriaVisible,
            onInputChange,
            onLetterClick,
            onToggleMoreCriteria,
            platform,
            reviews,
            selectedLetter,
            year,
        } = this.props;
        const advancedRule = this.moreCriteriaVisible ? null : advancedCriteria;

        const years = (() => {
            if (!platform) {
                return [];
            }

            const minYear = min(
                Object.keys(platform.releaseDates).map(key =>
                    moment(new Date(platform.releaseDates[key])).year()
                )
            );

            const maxYear = max(
                Object.keys(platform.discontinuationDates).map(key =>
                    moment(new Date(platform.discontinuationDates[key])).year()
                )
            );

            return range(minYear, maxYear + 1).map(year => ({
                label: year,
                value: year,
            }));
        })();

        return (
            <div { ...containerRule }>
                {platform ?
                    <Helmet
                        title={ `${platform.name} reviews` }
                        meta={ [
                            {
                                name: 'description',
                                content: `${platform.name} reviews`,
                            },
                            {
                                property: 'og:url',
                                content: `${WEBSITE_BASE_URL}/platforms/${platform.slug}`,
                            },
                            {
                                property: 'og:type',
                                content: 'article',
                            },
                            {
                                property: 'og:title',
                                content: 'supagemu.com',
                            },
                            {
                                property: 'og:description',
                                content: `${platform.name} reviews`,
                            },
                            {
                                property: 'og:image',
                                content: `${platform.image && platform.image.secure_url}`,
                            },
                        ] }
                    /> :
                    null}

                <PlatformDetails platform={ platform } />

                <div className="visible-xs" { ...inlineLoader }>
                    <Loader />
                </div>

                <h4 { ...subtitleRule }>
                    <Title boldText="FIND" normalText=" A GAME REVIEW" />
                </h4>

                <div { ...searchPanelRule }>

                    <SearchPanel>
                        <Row>
                            <Col md={ 11 }>
                                <Row>
                                    <Col
                                        md={ 8 }
                                        { ...searchInputRule }
                                        { ...inputSpacer }
                                    >
                                        <SearchInput
                                            callback={ onInputChange.bind(
                                                this,
                                                'text'
                                            ) }
                                            placeholder="Search for a game title..."
                                            icon
                                        />
                                    </Col>

                                    <div { ...advancedRule }>
                                        <Col md={ 4 } { ...inputSpacer }>
                                            <Select
                                                name="developer"
                                                value={ developer }
                                                options={ developers }
                                                onChange={ onInputChange.bind(
                                                    this,
                                                    'developer'
                                                ) }
                                                placeholder="All developers"
                                                labelKey="name"
                                                valueKey="id"
                                            />
                                        </Col>
                                    </div>
                                </Row>

                                <div { ...topBufferRule } />

                                <Row>
                                    <div { ...advancedRule }>
                                        <Col md={ 4 } { ...inputSpacer }>
                                            <Select
                                                name="genres"
                                                options={ genres }
                                                onChange={ onInputChange.bind(
                                                    this,
                                                    'genre'
                                                ) }
                                                value={ genre }
                                                placeholder="Genres"
                                                labelKey="name"
                                                valueKey="id"
                                                optionRenderer={ option => (
                                                    <Genre genre={ option } />
                                                ) }
                                                valueRenderer={ option => (
                                                    <Genre genre={ option } />
                                                ) }
                                            />
                                        </Col>
                                        <Col md={ 4 } { ...inputSpacer }>
                                            <Select
                                                name="year"
                                                value={ year }
                                                options={ years }
                                                onChange={ onInputChange.bind(
                                                    this,
                                                    'year'
                                                ) }
                                                placeholder="Year"
                                                labelKey="label"
                                                valueKey="value"
                                            />
                                        </Col>
                                        <Col md={ 4 } { ...inputSpacer }>
                                            <Select
                                                name="author"
                                                value={ author }
                                                options={ authors }
                                                onChange={ onInputChange.bind(
                                                    this,
                                                    'author'
                                                ) }
                                                placeholder="Review author"
                                                labelKey="name"
                                                valueKey="id"
                                                optionRenderer={ option => (
                                                    <Contributor
                                                        smallAvatar
                                                        user={ option }
                                                        inline
                                                    />
                                                ) }
                                                valueRenderer={ option => (
                                                    <div { ...contributorRule }>
                                                        <Contributor
                                                            smallAvatar
                                                            user={ option }
                                                            inline
                                                        />
                                                    </div>
                                                ) }
                                            />
                                        </Col>
                                    </div>
                                </Row>
                                <Clearfix />
                            </Col>
                            <Col md={ 1 } className="visible-md">
                                <Loader />
                            </Col>
                        </Row>
                        <Row { ...lettersContainer } { ...advancedCriteria }>
                            <Col md={ 12 }>
                                <ul { ...lettersRule }>
                                    {'abcdefghijklmnopqrstuvwxyz'
                                        .split('')
                                        .map(letter => (
                                            <li
                                                { ...letterItemRule }
                                                key={ letter }
                                            >
                                                <SearchLetter
                                                    letter={ letter }
                                                    handleClick={ onLetterClick.bind(
                                                        this,
                                                        letter
                                                    ) }
                                                    selected={ selectedLetter ===
                                                            letter }
                                                />
                                            </li>
                                        ))}
                                </ul>
                            </Col>
                        </Row>

                        <div { ...moreCriteria }>
                            <FontAwesome name="plus" className="icon" />
                            <a
                                role="button"
                                onClick={ onToggleMoreCriteria.bind(this) }
                            >
                                {moreCriteriaVisible ?
                                    'Less criteria' :
                                    'More criteria'}
                            </a>
                        </div>
                    </SearchPanel>

                </div>

                <ReviewList reviews={ reviews } displayAbout summary="full" />

            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/content/platform/PlatformMain.js